// usecase → feature/procedure 統合（#884）のクライアント側土台。
// test_designer `src/lib/sstf/procedure/{server.ts,usecase.ts}` と `src/lib/sstf/params.ts` の移植。
//
// 新モデル: 旧「usecase」= View 直下の feature ターゲット（type 空 + mainProcedureId あり）＋その main Procedure。
//   feature = 名前/説明/親子を持つ薄い箱。steps/parameters/policies/externalId(=旧usecaseId) は
//   main Procedure の value blob（フラット）に入る。

import {
  SSTFApiClient,
  GraphQLProcedure,
  GraphQLTestTarget,
  CreateProcedureInput,
  UpdateProcedureInput,
} from "./api";
import { UseCaseStep, ParameterValue, PolicyChange } from "../types/TestTarget";

// ==== value blob ヘルパー（params.ts フラット版の移植）====

/** JSON 文字列 / オブジェクトを plain object に正規化する（配列・スカラー・パース失敗は {}）。 */
export function coerceToObject(value: unknown): Record<string, unknown> {
  if (typeof value === "string") {
    try {
      const parsed: unknown = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : {};
    } catch {
      return {};
    }
  }
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/**
 * SSTF value ブロブを組み立てる（新規テーブル用・poc サブキーを使わず全キーを value 直下へ置く）。
 * topEntries は明示値（undefined は existingValue の同キーを引き継ぐ）、flatKeys は input から
 * value 直下へマージするキー（undefined でないキーのみ上書き）。旧 poc ラッパーは解体して落とす。
 */
export function buildValueBlobFlat(
  topEntries: Record<string, unknown>,
  flatKeys: readonly string[],
  input: Record<string, unknown>,
  existingValue?: string | null
): string | undefined {
  const { poc: _dropPoc, ...base } = coerceToObject(existingValue ?? undefined);
  const blob: Record<string, unknown> = { ...base };
  for (const [key, v] of Object.entries(topEntries)) {
    const resolved = v !== undefined ? v : base[key];
    if (resolved !== undefined) blob[key] = resolved;
  }
  for (const key of flatKeys) {
    if (input[key] !== undefined) blob[key] = input[key];
  }
  return Object.keys(blob).length > 0 ? JSON.stringify(blob) : undefined;
}

/**
 * value ブロブを rich フィールドへ unpack する（新規テーブル用・poc サブキーは読まない）。
 * defaults のキーを value 直下から読み（無ければ既定値）、それ以外の value 直下キーもそのまま展開する。
 */
export function parseValueBlobFlat<T>(
  value: string | null | undefined,
  defaults: Record<string, unknown>
): T {
  const { poc: _dropPoc, ...base } = coerceToObject(value ?? undefined);
  const out: Record<string, unknown> = { ...base };
  for (const [key, dflt] of Object.entries(defaults)) {
    out[key] = base[key] ?? dflt;
  }
  return out as T;
}

// ==== Procedure value 型 ====

/** 手順ポリシー。旧 ProcedurePolicy テーブルを廃し Procedure value（policies）へ畳み込む。 */
export type ProcedurePolicy = {
  policyId: string;
  name: string;
  description?: string;
  status?: string;
  changes: PolicyChange;
  parameters?: ParameterValue[];
};

/** Procedure value blob の poc 固有フィールド。 */
export type ProcedurePoc = {
  externalId?: string | null;
  /** 'main' | 'preparation' | 'transition' */
  type?: string;
  status?: string | null;
  inheritFromId?: string | null;
  /** main（ユースケース）手順の親 UC 参照。UC 親子ツリー用の構造リンク。 */
  parentUsecaseId?: string | null;
  source?: string;
  preconditions?: string[];
  /** Runner.id（Runner 未移行 = Int のまま value に保持） */
  runnerId?: number | null;
  generationQuality?: unknown | null;
  policies?: ProcedurePolicy[];
};

/** value blob 組み立ての入力。steps/parameters はトップレベル、その他は value 直下フラット。 */
export type ProcedureValueInput = ProcedurePoc & {
  steps?: UseCaseStep[];
  parameters?: ParameterValue[];
};

/** value blob を unpack した rich フィールド一式。 */
export type ParsedProcedure = ProcedurePoc & {
  steps: UseCaseStep[];
  parameters: ParameterValue[];
};

// inheritFromId / type は value に書かない（native inherit_from_parent と FK が真実の源）。
const PROC_FLAT_KEYS = [
  "externalId",
  "status",
  "parentUsecaseId",
  "source",
  "preconditions",
  "runnerId",
  "generationQuality",
  "policies",
] as const;

/**
 * Procedure の value ブロブを組み立てる。value 直下に steps/parameters（手順本体）と
 * PROC_FLAT_KEYS を全て置く。existingValue があればベースにし、指定キーのみ上書きする。
 */
export function buildProcedureValueBlob(
  input: ProcedureValueInput,
  existingValue?: string | null
): string | undefined {
  return buildValueBlobFlat(
    { steps: input.steps, parameters: input.parameters },
    PROC_FLAT_KEYS,
    input as Record<string, unknown>,
    existingValue
  );
}

/** value ブロブを rich フィールドへ unpack する。 */
export function parseProcedureValue(
  value: string | null | undefined
): ParsedProcedure {
  return parseValueBlobFlat<ParsedProcedure>(value, {
    steps: [],
    parameters: [],
  });
}

/** 既存 GraphQLProcedure を完全置換 update 用の入力へエコーし、overrides で上書きする。 */
export function echoProcedureUpdate(
  existing: GraphQLProcedure,
  overrides: Partial<Omit<UpdateProcedureInput, "id">> = {}
): UpdateProcedureInput {
  const defined = Object.fromEntries(
    Object.entries(overrides).filter(([, v]) => v !== undefined)
  ) as Partial<UpdateProcedureInput>;
  return {
    id: existing.id,
    name: existing.name,
    displayOrder: existing.displayOrder,
    inheritFromParent: existing.inheritFromParent,
    ...(existing.value != null ? { value: existing.value } : {}),
    ...defined,
  };
}

// ==== feature 判定 ====

/** GraphQLTestTarget の type 名（空文字/未設定を "" に正規化）。 */
function typeName(t: GraphQLTestTarget): string {
  return (t.type?.name ?? "").trim();
}

/** main procedure の SSTF UUID を取り出す。 */
export function mainProcedureIdOf(t: GraphQLTestTarget): string | null {
  return t.mainProcedure?.id ?? null;
}

/**
 * feature ターゲット判定。SSTF に `feature` 型は無く、
 * 「type が空（type-less）の子ターゲット かつ mainProcedureId を持つ」もの。
 */
export function isFeatureTarget(t: GraphQLTestTarget): boolean {
  return typeName(t) === "" && !!mainProcedureIdOf(t);
}

// ==== usecase（feature + main procedure）合成 ====

/**
 * 旧「usecase」の合成ビュー。feature ターゲット＋main procedure から作る。
 * SSTF に feature 型は無いため、usecase は「mainProcedureId を持つ子」で識別する。
 */
export interface UsecaseView {
  /** feature ターゲットの testTargetId（UUID）。詳細ページの内部 id 相当 */
  id: string;
  featureTargetId: string;
  /** usecaseId（poc 由来の externalId）。条件・ステップ参照のキー */
  usecaseId: string;
  name: string;
  title: string;
  description: string;
  steps: UseCaseStep[];
  parameters: ParameterValue[];
  type: string;
  status: string | null;
  parentUsecaseId?: string;
  policies: ProcedurePolicy[];
  /** main procedure の UUID（update 時のエコー用） */
  procedureId: string;
  /** feature ターゲットの親 View id */
  parentTestTargetId?: string;
  displayOrder: number;
  /** feature ターゲットの typeId（update の完全置換エコー用）。feature は type-less のため通常 null。 */
  featureTypeId: string | null;
  /** feature ターゲットの value(JSON文字列)。update 時に既存 value を維持するためエコーする。 */
  featureValue: string | null;
}

/** feature ターゲット＋main procedure から usecase ビューを合成する。 */
export function composeUsecase(
  featureTarget: GraphQLTestTarget,
  mainProc: GraphQLProcedure
): UsecaseView {
  const v = parseProcedureValue(mainProc.value);
  return {
    id: featureTarget.testTargetId,
    featureTargetId: featureTarget.testTargetId,
    usecaseId: v.externalId ?? featureTarget.testTargetId,
    name: mainProc.name,
    title: featureTarget.name,
    description: featureTarget.description ?? "",
    steps: v.steps,
    parameters: v.parameters,
    type: v.type ?? "main",
    status: v.status ?? null,
    parentUsecaseId: v.parentUsecaseId ?? undefined,
    policies: v.policies ?? [],
    procedureId: mainProc.id,
    parentTestTargetId: featureTarget.parentTestTargetId,
    displayOrder: featureTarget.displayOrder,
    // feature は type-less。type.typeId が無ければ null（空文字は UUID キャスト不可のため送らない）。
    featureTypeId: featureTarget.type?.typeId || null,
    featureValue: featureTarget.value ?? featureTarget.params ?? null,
  };
}

/**
 * 親 View 直下で main procedure を持つ子（= usecase）の一覧を SSTF から合成して返す。
 * allTargets を渡すと再取得を避ける（画面側で既に取得済みのケース向け）。
 */
export async function fetchUsecases(
  apiClient: SSTFApiClient,
  workspaceId: string,
  projectId: string,
  viewTargetId: string,
  allTargets?: GraphQLTestTarget[]
): Promise<UsecaseView[]> {
  const targets =
    allTargets ?? (await apiClient.getTestTargets(workspaceId, projectId));
  const features = targets.filter(
    (t) => t.parentTestTargetId === viewTargetId && isFeatureTarget(t)
  );
  const procIds = features
    .map((f) => mainProcedureIdOf(f))
    .filter((id): id is string => Boolean(id));
  const procs = await apiClient.getProcedures(workspaceId, projectId, procIds);
  const procById = new Map(procs.map((p) => [p.id, p]));
  return features
    .map((f) => {
      const procId = mainProcedureIdOf(f);
      const proc = procId ? procById.get(procId) : undefined;
      return proc ? composeUsecase(f, proc) : null;
    })
    .filter((u): u is UsecaseView => u !== null)
    .sort((a, b) => a.displayOrder - b.displayOrder);
}

/** ucId（usecaseId or featureTargetId）で単一 usecase を引く。 */
export async function findUsecase(
  apiClient: SSTFApiClient,
  workspaceId: string,
  projectId: string,
  viewTargetId: string,
  ucId: string,
  allTargets?: GraphQLTestTarget[]
): Promise<UsecaseView | null> {
  const usecases = await fetchUsecases(
    apiClient,
    workspaceId,
    projectId,
    viewTargetId,
    allTargets
  );
  return (
    usecases.find((u) => u.usecaseId === ucId || u.featureTargetId === ucId) ??
    null
  );
}

/** CreateProcedureInput を re-export（呼び出し側の型利用向け）。 */
export type { CreateProcedureInput, UpdateProcedureInput };
